/**
 * One message for both fields, whichever was wrong.
 *
 * The route's own summary says so. Telling somebody the address exists but the
 * password is wrong is telling anybody who asks which addresses exist.
 */
export const CREDENTIALS_WRONG = 'That email address and password do not match.'

export const CODE_WRONG = 'That code is not right. Codes change every thirty seconds.'

/**
 * Why the desk would not let somebody in, told truthfully.
 *
 * Both handlers on 1.10 were `catch { … }` with one sentence each: every
 * failure on the password step said *"that email address and password do not
 * match"*, and every failure on the code step said the code was wrong. Found
 * by running the screen rather than by reading it — the first sign-in attempt
 * in a browser failed before the request left the page at all, and the desk
 * said the password was wrong.
 *
 * Four of these are things the person can act on and one is not their fault:
 *
 * - a suspended account is not a typo, and retyping the password forever is
 *   what somebody does when told it does not match;
 * - an account that was never verified needs the code from signing up, not a
 *   different password;
 * - the lockout has a wait, and the server says how long;
 * - a connection that is not there is the desk's problem, not theirs.
 *
 * Anything the server explained in its own words keeps those words — the code
 * step's *"that code is not right, 3 left"* counts down, and overwriting it
 * with a constant threw the count away.
 */
export const WHY_REFUSED: Record<string, string> = {
  CREDENTIALS_INVALID: CREDENTIALS_WRONG,
  ACCOUNT_SUSPENDED:
    'This account has been suspended. Retyping the password will not help. Ask whoever manages administrators.',
  ACCOUNT_UNVERIFIED:
    'This account was never finished. Open the app and enter the code sent when it was created.',
  ROLE_FORBIDDEN: 'This account is not an administrator, so it cannot use this desk.',
  TOO_MANY_ATTEMPTS: 'Too many attempts. Wait a little and try again.',
  CODE_EXPIRED: 'That took too long. Start again.',
  /* Confirming a second factor twice. Seen for real: the first confirmation
     was accepted and its answer never reached the page, so pressing the
     button again said the code was wrong about a code that had worked. */
  CONFLICT: 'That is already set up. Reload this page and sign in with a code from your app.',
  NOT_FOUND: 'There is nothing to confirm. Start again from your password.',
  UNAUTHENTICATED: 'That took too long. Start again from your password.',
  NETWORK: 'No connection. The desk could not reach the server, so nothing was sent.',
  INTERNAL: 'Something went wrong at our end. Nothing has changed; try again.',
}

/** Why there is a second step at all, said once, where it appears. */
export const WHY_A_SECOND_STEP =
  'This desk can move money and read people’s records, so it asks for a code from an authenticator app as well as a password.'

/**
 * Which apps work, and **not** a third instruction to scan something.
 *
 * The heading said "Set up your authenticator app", the step under it said
 * "Scan this with your authenticator app", and this said "Scan this with an
 * authenticator app" — the same sentence three times in four lines, which is
 * the fault this desk keeps finding and had written into its own front door.
 * The heading names the task, the step names the action, and this names the
 * apps. One job each.
 */
export const SCAN_IT = '1Password, Authy and Google Authenticator all work.'

/**
 * The three steps, named.
 *
 * This screen used to be seven blocks stacked in a column with one sentence
 * over them: a heading, a paragraph, a QR, a key, a warning banner, ten
 * recovery codes and six digit boxes. Every one of them looked equally
 * important and nothing said which to do first — on the one screen somebody
 * meets exactly once, under time pressure, with their phone in their hand.
 *
 * They are three things, so they are numbered. The length is the same; what
 * changed is that it now reads as a sequence somebody is partway through.
 */
export const ENROL_STEPS = [
  { title: 'Scan this', next: 'Next' },
  /* Not "Next". The button is the acknowledgement — these are shown once and
     the screen does not come back, so pressing it should be a sentence
     somebody would not press without meaning it. */
  { title: 'Keep your recovery codes', next: 'I have saved them' },
  { title: 'Enter the six digits it shows', next: null },
] as const

/** "Step 2 of 3" — how far in, and how much is left. */
export const stepCount = (n: number) => `Step ${n + 1} of ${ENROL_STEPS.length}`

/** The key, for a camera that will not read or a machine with no camera. */
export const NO_CAMERA = 'Cannot scan it?'
export const TYPE_THE_KEY = 'Enter this key in the app by hand instead.'

/**
 * 1.8, and it is deliberately the same answer either way.
 *
 * *"This screen is what going wrong looks like, and it is deliberately the
 * same whether or not the address is registered."* An acknowledgement that
 * varied would be a way to find out who has an account.
 */
export const RESET_SENT =
  'If that address has an account, a reset link is on its way. The link expires in one hour.'

/**
 * 1.11's own line: which account is being changed.
 *
 * Used to say "Once it is saved you will be signed in on this device" — true
 * in the sense that a reset ends in a session eventually, but every desk
 * account owes a second factor, so what actually happened next was *always*
 * one more screen (a code, or setting one up the first time), never the desk
 * itself. Said plainly now, so that screen reads as the promise keeping
 * itself rather than as something having gone wrong (the client saw exactly
 * this: "kept me in the same page, user will get confused if it got reset or
 * not").
 */
export const RESET_SIGNS_YOU_IN =
  'Once it is saved, you will be asked for your six-digit code (or to set one up, the first time) before you are signed in.'

/**
 * Said once, on the screen straight after a reset succeeds (the code step or
 * the first-time set-up), so nobody wonders whether the password before it
 * actually saved. Not shown on an ordinary sign-in's own code step — only
 * after a reset, which is the one path that used to leave this unsaid.
 */
export const PASSWORD_JUST_CHANGED = 'Your password has been changed.'

/**
 * The password **was** changed, and the step after it was not.
 *
 * The first administrator ever created on the live desk set their password,
 * and was shown *"That link has expired."* It had not. The reset had already
 * succeeded — the new password was in the database — and the screen then tried
 * to treat the answer as a session.
 *
 * It is not one. An administrator's reset returns a **second-factor
 * challenge**, exactly as signing in does, because `resetPassword` ends in
 * `issueSessionOrChallenge` for precisely that reason: *"anyone who could
 * reach an admin's inbox could have taken the account with no second factor
 * at all."* The screen read `access_token` off a challenge, threw, and the
 * `.catch` that was meant for a dead link reported a dead link.
 *
 * So there is now a sentence for the one case that used to be impossible to
 * tell apart: the password is changed, the link is spent, and something after
 * it went wrong. Saying "expired" there sends somebody to request a second
 * link they do not need, and the password they just chose already works.
 */
export const PASSWORD_CHANGED_ANYWAY =
  'Your password was changed. Something went wrong setting up the next step. Sign in with the new password and it will pick up where it left off.'

/**
 * 1.12 explains the rule rather than reporting the failure.
 *
 * A link that stopped working is somebody's second bad minute; saying *why*
 * an hour is the window is the difference between a wall and a reason.
 */
export const LINK_EXPIRED =
  'Reset links last one hour, so an old one sitting in an inbox cannot be used by somebody else. Ask for a new one and it will arrive in a moment.'

export const PASSWORD_RULE = 'At least 8 characters, with one number.'
export const PASSWORDS_DIFFER = 'Those two passwords are not the same.'
